import type { OperatingHours } from './api'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function clock(value: string): string {
  const [rawHour, rawMinute] = value.split(':').map(Number)
  if (!Number.isFinite(rawHour) || !Number.isFinite(rawMinute)) return value
  const suffix = rawHour >= 12 ? 'PM' : 'AM'
  const hour = rawHour % 12 || 12
  return `${hour}:${String(rawMinute).padStart(2, '0')} ${suffix}`
}

function sameSlot(a: { open: string; close: string } | null | undefined, b: { open: string; close: string } | null | undefined): boolean {
  return !!a && !!b && a.open === b.open && a.close === b.close
}

/** Human-readable schedule; raw HH:MM values remain the source of truth. */
export function formatValidationHours(hours?: OperatingHours): string {
  if (!hours) return 'Available any time'

  const groups: Array<{ start: number; end: number; slot: { open: string; close: string } }> = []
  for (let day = 0; day < 7; day += 1) {
    const slot = hours[day]
    if (!slot) continue
    const previous = groups[groups.length - 1]
    if (previous && previous.end === day - 1 && sameSlot(previous.slot, slot)) {
      previous.end = day
    } else {
      groups.push({ start: day, end: day, slot })
    }
  }

  if (groups.length === 0) return 'No days scheduled'
  return groups.map(({ start, end, slot }) => {
    const days = start === end ? DAY_NAMES[start] : `${DAY_NAMES[start]}–${DAY_NAMES[end]}`
    return `${days} ${clock(slot.open)}–${clock(slot.close)}`
  }).join(' · ')
}

/**
 * "3:05 PM PDT" in the lot's timezone, or `null` when the server named none;
 * the caller then omits the clock rather than print the viewer's own.
 */
export function lotClockLabel(at: Date, timeZone: string | undefined): string | null {
  if (!timeZone) return null
  try {
    return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone, timeZoneName: 'short' })
  } catch {
    return null   // an unknown IANA name: say nothing rather than guess
  }
}

/** "PDT", for the "zone local time" note on an offer card. */
export function lotZoneAbbrev(at: Date, timeZone: string | undefined): string | null {
  if (!timeZone) return null
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(at).find(p => p.type === 'timeZoneName')?.value ?? null
  } catch {
    return null
  }
}

/**
 * A program's window with the lot's zone named: "Mon–Fri 8:00 AM–6:00 PM PDT".
 * `activeHours` are wall-clock times in the LOT's timezone (the server's
 * `timezone` field, same anchor redemption enforces). Without that field the
 * zone is left off rather than implying the viewer's own clock.
 */
export function formatLotValidationHours(hours: OperatingHours, timeZone: string | undefined, at = new Date()): string {
  const schedule = formatValidationHours(hours)
  const zone = lotZoneAbbrev(at, timeZone)
  return zone && schedule !== 'No days scheduled' ? `${schedule} ${zone}` : schedule
}
